// inventorySparesApiSlice.js
import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "../../apiSlice";

/* ------------------ Adapter ------------------ */
const inventorySparesAdapter = createEntityAdapter({
  selectId: (item) => item.request_id,
});

const initialState = inventorySparesAdapter.getInitialState();

/* ------------------ API Slice ------------------ */
export const inventorySparesListSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* -------- LIST -------- */
    getInventorySparesList: builder.query({
      query: ({ seller_id, status } = {}) => {
        let queryString = "/inventory/spares";
        const params = new URLSearchParams();

        if (seller_id) params.append("seller_id", seller_id);
        if (status) params.append("status", status);

        if (params.toString()) {
          queryString += `?${params.toString()}`;
        }

        return queryString;
      },

      transformResponse: (response) =>
        inventorySparesAdapter.setAll(initialState, response.data),

      providesTags: (result) =>
        result
          ? [
              { type: "InventorySpare", id: "LIST" },
              ...result.ids.map((id) => ({
                type: "InventorySpare",
                id,
              })),
            ]
          : [{ type: "InventorySpare", id: "LIST" }],
    }),

    /* -------- GET BY ID -------- */
    getInventorySpareById: builder.query({
      query: (id) => `/inventory/spares/${id}`,
      providesTags: (result, error, id) => [{ type: "InventorySpare", id }],
    }),

    /* -------- UPDATE -------- */
    updateInventorySpareById: builder.mutation({
      query: ({ request_id, data }) => ({
        url: `/inventory/spares/${request_id}`,
        method: "PATCH",
        body: data,
      }),

      invalidatesTags: (result, error, { request_id }) => [
        { type: "InventorySpare", id: request_id },
        { type: "InventorySpare", id: "LIST" },
      ],
    }),

    /* -------- FILE UPLOAD -------- */
    uploadInventorySpareFile: builder.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: "/inventory/spares/upload_file",
          method: "POST",
          body: formData,
        };
      },

      invalidatesTags: [{ type: "InventorySpare", id: "LIST" }],
    }),
    /* -------- DELETE -------- */
    deleteInventorySpareById: builder.mutation({
      query: (request_id) => ({
        url: `/inventory/spares/${request_id}`,
        method: "DELETE",
      }),

      invalidatesTags: (result, error, request_id) => [
        { type: "InventorySpare", id: request_id },
        { type: "InventorySpare", id: "LIST" },
      ],
    }),
  }),
});

/* ------------------ Hooks ------------------ */

export const {
  useGetInventorySparesListQuery,
  useGetInventorySpareByIdQuery,
  useUpdateInventorySpareByIdMutation,
  useUploadInventorySpareFileMutation,
  useDeleteInventorySpareByIdMutation
} = inventorySparesListSlice;

/* ------------------ Selectors ------------------ */

const inventorySpareFilter = (state) => state.inventorySpareFilter;

const selectInventorySparesQueryResult = createSelector(
  [inventorySpareFilter, (state) => state],
  (filters, state) => {
    return inventorySparesListSlice.endpoints.getInventorySparesList.select({
      seller_id: filters.seller_id,
      status: filters.status,
    })(state);
  },
);

const selectInventorySparesData = createSelector(
  [selectInventorySparesQueryResult],
  (queryResult) => queryResult?.data ?? initialState,
);

/* ------------------ Entity Adapter Selectors ------------------ */

export const {
  selectAll: selectInventorySpares,
  selectById: selectInventorySpareById,
  selectIds: selectInventorySpareIds,
} = inventorySparesAdapter.getSelectors((state) =>
  selectInventorySparesData(state),
);
